import { observer } from 'mobx-react'
import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

// Components
import { DataTable } from '../../components/DataTable'
import { PageContainer } from '../../components/PageContainer'
import { StateContainer } from '../../components/StateContainer'
import { TextEditor } from '../../components/CustomEditor'

// Services
import { getClient } from '../../services/clients.service'
import { 
  getClientPortfolios, 
  updateClientPortfolio, 
  deleteClientPortfolio,
  getPortfolioPortfolioDataCount 
} from '../../services/portfolios.service'

// Stores
import { ClientDashboardStoreContext } from '../../stores/ClientDashboardStore'

// Utils & Hooks
import { toast } from '../../utils/helpers'
import { usePagination, useSorting } from '../../hooks/DataTableManagement'

/**
 *
 * Portfolios
 *
 */
const Portfolios = observer(() => {
  // Context
  const { clientId } = useParams()
  const { setClient } = useContext(ClientDashboardStoreContext)

  // Pagination
  const { pagination, setTotalRecords } = usePagination(10)
  const { perPage, currentPage } = pagination

  // Sorting
  const { sorting } = useSorting('name_sort')
  const { sortedColumn } = sorting

  // State
  const [loading, setLoading] = useState(true)
  const [loadingPortfolios, setLoadingPortfolios] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [error, setError] = useState(null)
  const [portfolios, setPortfolios] = useState([])
  const [portfolioDataCounts, setPortfolioDataCounts] = useState({})

  const BASE_URL = `/clients/${clientId}/portfolios/?`

  const handleSuccess = (m) => toast(m, 'success')
  const handleErrors = (m) => toast(m, 'error')

  /**
   * Gets the updated list of portfolios; updates pagination and portfolio data counts.
   * @param {string} url
   */
  const getUpdatedPortfolios = async (url) => {
    const response = await getClientPortfolios(url, setError, setLoadingPortfolios)

    if (response) {
      setTotalRecords(response.count)
      setPortfolios(response.results)

      // Fetch all portfolio data counts concurrently
      const countPromises = response.results.map(portfolio =>
        getPortfolioPortfolioDataCount(clientId, portfolio.id, () => {}, () => {})
      )
      const countResults = await Promise.all(countPromises)
      const counts = {}
      response.results.forEach((portfolio, idx) => {
        counts[portfolio.id] = countResults[idx]
      })
      setPortfolioDataCounts(counts)
    }
  }

  useEffect(() => {
    if (clientId) {
      const getUpdatedClient = async () => {
        const updatedClient = await getClient(clientId, setError, setLoading)

        if (updatedClient) {
          setClient(updatedClient)
        }
      }

      getUpdatedClient()
    }
  }, [clientId])

  /**
   * When the filter, sort, current page, or row count changes, get the updated list of portfolios.
   */
  useEffect(() => {
    if (clientId) {
      getUpdatedPortfolios(
        `${BASE_URL}&order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}`,
      )
    }
  }, [perPage, clientId, sortedColumn, currentPage])

  /**
   * Handles row edit completion by updating the local `portfolio` with new data and
   * submitting it to the backend.
   * @param {object} event
   */
  const onRowEditComplete = async ({ newData, index }) => {
    const oldPortfolios = [...portfolios]
    const updatedData = [...portfolios]
    updatedData[index] = newData

    // Optimistically update the data to prevent showing internal values
    setPortfolios(updatedData)

    await updateClientPortfolio(
      clientId,
      newData,
      (m) => {
        // Reset back to previous value
        setPortfolios(oldPortfolios)
        handleErrors(m)
      },
      () => {},
      (m) => handleSuccess(m),
    )
  }

  return (
    <PageContainer>
      <StateContainer error={error} loading={loading}>
        <div className="flex size-full flex-col overflow-y-auto bg-background">
          <div className="space-y-6">
            <h2 className="text-xl font-semibold leading-6 text-gray-900">Portfolios</h2>

            <DataTable
              data={portfolios}
              columns={[
                {
                  field: 'name',
                  header: 'Name',
                  editor: TextEditor,
                  style: { width: '50%' },
                  sortable: true,
                  sortField: 'name_sort',
                },
                {
                  field: 'number',
                  header: 'Number',
                  editor: TextEditor,
                  style: { width: '50%' },
                  sortable: true,
                  sortField: 'number_sort',
                },
              ]}
              hoverEffect={false}
              loading={loadingPortfolios}
              loadingDelete={loadingDelete}
              pagination={pagination}
              sorting={sorting}
              onRowEditComplete={onRowEditComplete}
              onRowDeleteConfirm={(row) => {
                deleteClientPortfolio(clientId, row.id, handleErrors, setLoadingDelete, () => {
                  handleSuccess('Portfolio deleted.')
                  getUpdatedPortfolios(
                    `${BASE_URL}&order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}`,
                  )
                })
              }}
              areRowsDeleteable
              areRowsEditable
              rowDeleteConfirm={(row) => {
                const portfolioCount = portfolioDataCounts[row.id] || 0
                const portfolioMessage =
                  portfolioCount > 0
                    ? ` This will delete ${portfolioCount} portfolio data record${
                        portfolioCount === 1 ? '' : 's'
                      } associated with this portfolio.`
                    : ''

                return {
                  title: `Delete Portfolio`,
                  type: 'warning',
                  message: (
                    <div className="flex flex-col">
                      <span className="">Are you sure you want to delete the portfolio:</span>
                      <span className="font-semibold">{row.name}</span>
                      {portfolioMessage && (
                        <div className="mt-2 rounded border-l-4 border-orange-400 bg-orange-50 p-2">
                          <span className="text-orange-800">{portfolioMessage}</span>
                        </div>
                      )}
                    </div>
                  ),
                }
              }}
            />
          </div>
        </div>
      </StateContainer>
    </PageContainer>
  )
})

export default Portfolios
